import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { StartLearningOnboardingFlow } from "../start-learning";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { getAddedLanguageSpacesStorageKey } from "../../hooks/useAddedLanguageSpaces";
import { ENABLED_LANGUAGE_SPACE_IDS } from "../../constants";
import { useMemo } from "react";
import { useLanguageSpace, type LanguageSpace } from "../../hooks/useLanguageSpace";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useUserSettingsUpdate } from "../../hooks/useUserSettingsUpdate";

export const Route = createFileRoute("/app/start-learning")({
  component: AppStartLearningPage,
});

function AppStartLearningPage() {
  const loginStatus = useLoginStatus();
  const userSettings = useUserSettings({
    enabled: loginStatus.data?.state === "logged in",
  });
  const updateUserSettings = useUserSettingsUpdate();
  const navigate = useNavigate();
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const addedLanguageSpacesStorageKey = getAddedLanguageSpacesStorageKey(
    loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null,
  );
  const [addedLanguageSpaces, setAddedLanguageSpaces] = useLocalStorage<string[]>(
    addedLanguageSpacesStorageKey,
    [],
  );
  const enabledLanguageSpaceSet = useMemo(
    () => new Set<string>(ENABLED_LANGUAGE_SPACE_IDS),
    [],
  );
  const serverAddedLanguageSpaces = Array.isArray(
    userSettings.data?.added_language_spaces,
  )
    ? userSettings.data.added_language_spaces.filter((id: string) =>
        enabledLanguageSpaceSet.has(id),
      )
    : [];
  const isForcedOnboarding = userSettings.data?.onboarding_completed !== true;

  return (
    <StartLearningOnboardingFlow
      embedded
      onClose={
        isForcedOnboarding
          ? undefined
          : () =>
              void navigate({
                to: "/app/reeprise",
                search: { mobileView: undefined },
              })
      }
      onFinish={async ({ selectedLanguageIds, selectedLanguageLevels }) => {
        const normalizedExisting = (Array.isArray(addedLanguageSpaces)
          ? addedLanguageSpaces
          : []
        ).filter((id): id is string => enabledLanguageSpaceSet.has(id));
        const normalizedServerExisting = serverAddedLanguageSpaces.filter((id) =>
          enabledLanguageSpaceSet.has(id),
        );
        const normalizedSelected = selectedLanguageIds.filter((id) =>
          enabledLanguageSpaceSet.has(id),
        );
        const nextLanguageLevels = {
          ...(userSettings.data?.language_levels ?? {}),
          ...Object.fromEntries(
            Object.entries(selectedLanguageLevels).filter(
              ([languageId, level]) =>
                enabledLanguageSpaceSet.has(languageId) && level.trim().length > 0,
            ),
          ),
        };
        const nextSpaces = Array.from(
          new Set([
            ...normalizedServerExisting,
            ...normalizedExisting,
            ...(languageSpace ? [languageSpace] : []),
            ...normalizedSelected,
          ]),
        );
        await updateUserSettings.mutateAsync({
          userSettingsDto: {
            added_language_spaces: nextSpaces,
            language_levels: nextLanguageLevels,
            onboarding_completed: true,
          },
        });
        setAddedLanguageSpaces(nextSpaces);
        const firstSelected = normalizedSelected[0];
        if (firstSelected) {
          setLanguageSpace(firstSelected as LanguageSpace);
        }
      }}
      onComplete={() => {
        void navigate({
          to: "/app/reeprise",
          search: { mobileView: undefined },
        });
      }}
    />
  );
}
